import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { LocationActions } from '@states/location/location.action-types';
import { catchError, concatMap, filter, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { combineLatest, exhaustMap, of, share } from 'rxjs';
import { select, Store } from '@ngrx/store';
import { LocationsService } from '../../locations/locations.service';
import { LocationModel } from '../../locations/location.model';
import { LocationSelectors } from '@states/location/location.selector-types';
import { EdgeActions } from '@states/edge/edge.action-types';
import { CameraActions } from '@states/camera/camera.action-types';
import { AppState } from '../app.state';
import { SharedActions } from '@states/shared/shared.action-types';
import { PulsationModels } from '@models/pulsation.model';
import { AuthenticationActions } from '@states/authentication/authentication.action-types';
import { LocalStorageService } from '@ui';
import { SensorsService } from 'src/app/services/sensors.service';
import { LocationStatusActions } from '@states/location-status/location-status.action-types';
import { TranslateService } from '@ngx-translate/core';
import { AppleTvService } from '../../services/apple-tv.service';
import { AccessDoorsService } from '../../services/access-doors.service';
import { HomeActions } from '@states/home/home.action-types';
import { LocationEditActions } from '@states/location-edit/location-edit.action-types';
import { DeviceLocationModel } from '../../pages/location-v2/pages/device/models';
import { RemoteIOService } from '../../services/remote-io.service';
import { ExternalStorageService } from '../../services/external-storage.service';
import { PhysicalAccessControlService } from '../../services/physical-access-control.service';
import { Update } from '@ngrx/entity/src/models';
import { Edge } from '../../edge/edge.model';
import ComponentStatusDisplay = PulsationModels.ComponentStatusDisplay;
import NodeType = DeviceLocationModel.NodeType;
import { concatLatestFrom } from '@ngrx/operators';
import { DatabaseTagsActions } from '@states/database-tags/database-tags.action.types';
import { DatabaseTagsService } from '../../services/database-tags.service';
import { UtilsService } from '../../edge/utils.service';
import { GetLocationsForGlobalSearch } from '@states/location/location.actions';
import { SearchableActions } from '@states/searchable/searchable.action-types';
import { HomeSelectors } from '@states/home/home.selector-types';

@Injectable()
export class LocationEffects {
  GetLocations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationActions.GetLocations),
      withLatestFrom(this.store.pipe(select(LocationSelectors.isFirstLocationLoaded))),
      filter(([action, isFirstLoaded]) => !isFirstLoaded),
      map(([action, isLocationsLoaded]) => action),
      concatMap(action => {
        return this.locationService.getAllLocations()
          .pipe(
            map(res => {
              return this.computeLocationLookup(res);
            }),
            switchMap(res => {
              return [
                LocationActions.GetLocationsSuccess({ payload: res.locations }),

                // EntityLookupActions.FlattenEntityLookup({ locations: res.locations }),
                // SearchableActions.FlattenSearchableLocations({ locations: res.locations }),
                // LocationActions.SetLocationsLookup({ lookup: res.locationLookup }),
                EdgeActions.GetLocationEdgesNoBackendCall({ payload: res.edges }),
                CameraActions.GetLocationEdgesCamerasSnapshots(),
                CameraActions.GetLocationEdgesCamerasSuccess({ payload: res.cameras }),
                HomeActions.getLocations(),
                DatabaseTagsActions.getDatabaseTags(),
                CameraActions.getProperFitting(),

              ];
            }),
            catchError(err => {
              const msg = err?.error?.message || 'uknown error occured get Locations Fail';
              return [
                LocationActions.GetLocationsFail({
                  message: msg,
                }),
                AuthenticationActions.Logout({ params: { msg } }),
              ];
            }),
          );
      }),
      catchError(err => {
        return of(
          LocationActions.GetLocationsFail({
            message: err?.error?.message || 'uknown error occured',
          }),
        );
      }),
    ),
  );

  CreateLocationBackendCall$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationActions.CreateLocationBackendCall),
      map(action => action.request),
      concatMap((request: LocationModel.LocationCreateRequest) => {
        return this.locationService.createLocation(request)
          .pipe(
            switchMap((res: LocationModel.LocationCreateResponse) => {
              if (!res._id) {
                const error: any = new Error(`Couldn't get location generated id`);
                error.timestamp = Date.now();
                throw error;
              }
              const location: LocationModel.LocationItem = {
                ...request,
                _id: res._id,
              };
              return [
                LocationActions.CreateLocationSuccess({
                  response: location,
                }),
                LocationEditActions.updateSelectedLocation({ updatedLocation: location }),
              ];
            }),
            catchError((err: Error) => of(LocationActions.CreateLocationFail({ message: err.message }))),
          );
      }),
    ),
  );

  CreateLocationNoBackendCall$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationActions.CreateLocationNoBackendCall),
      map(action => action.request),
      tap(res => {
        if (!res || !res._id) {
          const error: any = new Error(`location id is required`);
          error.timestamp = Date.now();
          throw error;
        }
      }),
      switchMap(response => {
        // new added location become automatically online
        return [
          LocationActions.CreateLocationSuccess({ response }),
          LocationStatusActions.setLocationStatusTimeout({
            locationStatus: {
              locationId: response._id,
              status: ComponentStatusDisplay.Online,
            },
          }),
          HomeActions.getLocations(),
          LocationEditActions.updateSelectedLocation({ updatedLocation: response }),
        ];
      }),
      catchError((err: Error) => of(LocationActions.CreateLocationFail({ message: err.message }))),
    ),
  );

  DeleteLocationNoBackendCall$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationActions.DeleteLocationNoBackendCall),
      mergeMap(action => {
        return combineLatest([of(action), this.store.pipe(select(LocationSelectors.selectLocationById(action.request.locationId)))]);
      }),
      filter(([action, locationItem]) => {
        return !!locationItem;
      }),
      map(([action, locationItem]) => {
        return LocationActions.DeleteLocationSuccess({
          response: {
            locationId: locationItem!._id,
            result: true,
          },
        });
      }),
      catchError((err: Error) => {
        return of(
          LocationActions.DeleteLocationFail({
            message: err.message || 'Unknown error occurred',
          }),
        );
      }),
    ),
  );

  DeleteLocation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationActions.DeleteLocation),
      map(action => action.request),
      concatMap((request: LocationModel.LocationDeleteRequest) => {
        return this.locationService.deleteLocation(request.locationId, true)
          .pipe(
            map((res: boolean) => {
              if (!res) {
                const error: any = new Error(`Couldn't get location generated id`);
                error.timestamp = Date.now();
                throw error;
              }

              return LocationActions.DeleteLocationSuccess({
                response: { result: res, locationId: request.locationId },
              });
            }),
            catchError((err: Error) => of(LocationActions.DeleteLocationFail({ message: err.message }))),
          );
      }),
    ),
  );

  DeleteCameraNoBackendCall$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationActions.DeleteCameraNoBackendCall),
      mergeMap(action => {
        return combineLatest([of(action), this.store.pipe(select(LocationSelectors.selectLocationById(action.request.locationId)))]);
      }),
      filter(([action, locationItem]) => {
        return !!locationItem;
      }),
      map(([action, locationItem]) => {
        return LocationActions.DeleteCameraSuccess({
          response: {
            location: locationItem!,
            ...action.request,
          },
        });
      }),
      catchError((err: Error) => {
        return of(
          LocationActions.DeleteCameraFail({
            message: err.message || this.translateService.instant('TOASTS.UNKNOWN_ERROR_OCCURRED'),
          }),
        );
      }),
    ),
  );

  /**
   *  edge info get
   */

  public getEdgeInfo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationActions.GetEdgeInfo),
      switchMap(({ locationId, edgeId }) => {
        return this.locationService.getEdgeInfo(locationId, edgeId)
          .pipe(
            switchMap(res => {
              return [
                LocationActions.SetEdgeInfo({
                  edgeInfo: res.data,
                }),
              ];
            }),
            catchError(response => {
              return [
                SharedActions.showMessage({ error: JSON.stringify(response) }),
                // SharedActions.setSomethingWentWrong({somethingWentWrong: true}),
              ];
            }),
          );
      }),
      share(),
    ),
  );

  public computeLocationLookup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationActions.computeLocationLookup),
      withLatestFrom(this.store.pipe(select(LocationSelectors.selectAllLocations))),
      switchMap(([{}, locations]) => {
        const res = this.computeLocationLookup(locations);
        return [LocationActions.SetLocationsLookup({ lookup: res.locationLookup })];
      }),
      share(),
    ),
  );

  public setViewType$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationActions.setViewType),
      switchMap(({ viewType }) => {
        this.localStorageService.setItem('viewType', viewType);
        return [];
      }),
      share(),
    ),
  );

  public getViewTypeFromLocalStorage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationActions.getViewTypeFromLocalStorage),
      switchMap(() => {
        const storedViewType = Number(this.localStorageService.getItem('viewType'));
        const viewType =
          storedViewType === LocationModel.ViewType.Home
          || storedViewType === LocationModel.ViewType.Devices
          || storedViewType === LocationModel.ViewType.Floors
          || storedViewType === LocationModel.ViewType.Map
            ? storedViewType
            : LocationModel.ViewType.Home;

        return [LocationActions.getViewTypeFromLocalStorageSuccess({ viewType })];
      }),
      share(),
    ),
  );

  public getSensors$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationActions.GetSensors),
      exhaustMap(() => {
        return this.sensorsService.getAllSensors()
          .pipe(
            map(sensors => LocationActions.GetSensorsSuccess({ sensors })),
          );
      }),
    ),
  );

  public onTagRemoved$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DatabaseTagsActions.onTagRemoved),
      withLatestFrom(this.store.pipe(select(LocationSelectors.selectAllLocations))),
      map(([{ id }, locations]) => {
        return this.computeLocationsOnTagRemoved(locations, id);
      }),
      switchMap((res) => {
        return [
          LocationActions.RemoveTagFromLocations({ updates: res.locationUpdates }),
          CameraActions.RemoveTagFromCameras({ updates: res.cameraUpdates }),
          EdgeActions.removeTagFromEdges({ updates: res.edgeUpdates }),
        ];
      }),
      share(),
    ),
  );

  private computeLocationLookup(res: LocationModel.LocationItem[]) {
    const locationLookup = {};
    const edges = res
      .map(e => {
        locationLookup[e._id] = e;
        return !!e.edges ? Object.values(e.edges!) : [];
      })
      .flat();
    const cameras = !!edges?.length ? edges.map(e => (!!e.cameras ? Object.values(e.cameras!) : []))
      .flat() : [];
    return {
      locations: res,
      edges,
      cameras,
      locationLookup,
    };
  }

  private computeLocationsOnTagRemoved(
    locationItems: LocationModel.LocationItem[],
    tagId: string,
  ): {
    locationUpdates: Update<LocationModel.LocationItem>[],
    edgeUpdates: Update<Edge.EdgeItem>[],
    cameraUpdates: Update<LocationModel.LocationCameraItem>[],
  } {
    const locationUpdates: Update<LocationModel.LocationItem>[] = [];
    const edgeUpdates: Update<Edge.EdgeItem>[] = [];
    const cameraUpdates: Update<LocationModel.LocationCameraItem>[] = [];

    for (const loc of locationItems) {
      let locChanged = false;

      // Update location tags
      const newLocationTags = (loc.tags ?? []).filter(t => t !== tagId);
      if (newLocationTags.length !== (loc.tags?.length ?? 0)) {
        locChanged = true;
      }

      const updatedEdges: Edge.EdgeMap = {};

      if (loc.edges) {
        for (const [edgeId, edge] of Object.entries(loc.edges)) {
          let edgeChanged = false;

          // Update edge tags
          const newEdgeTags = (edge.tags ?? []).filter(t => t !== tagId);
          if (newEdgeTags.length !== (edge.tags?.length ?? 0)) {
            edgeChanged = true;
            edgeUpdates.push({
              id: edge.edgeId!,
              changes: { tags: newEdgeTags },
            });
          }

          const updatedCameras: LocationModel.LocationCamerasMap = {};

          if (edge.cameras) {
            for (const [cameraId, cam] of Object.entries(edge.cameras)) {
              const newCameraTags = (cam.edgeOnly.tags ?? []).filter(t => t !== tagId);

              let camChanged = false;
              if (newCameraTags.length !== (cam.edgeOnly.tags?.length ?? 0)) {
                camChanged = true;

                cameraUpdates.push({
                  id: cam.edgeOnly.cameraId!,
                  changes: {
                    edgeOnly: {
                      ...cam.edgeOnly,
                      tags: newCameraTags,
                    },
                  },
                });
              }

              const updatedCamera = camChanged
                ? {
                  ...cam,
                  edgeOnly: { ...cam.edgeOnly, tags: newCameraTags },
                }
                : cam;

              updatedCameras[cameraId] = updatedCamera;
              if (camChanged) edgeChanged = true;
            }
          }

          const updatedEdge = edgeChanged
            ? {
              ...edge,
              tags: newEdgeTags,
              cameras: updatedCameras,
            }
            : edge;

          updatedEdges[edgeId] = updatedEdge;

          if (edgeChanged) locChanged = true;
        }
      }

      // Build final Location update
      if (locChanged) {
        const updatedLocation: LocationModel.LocationItem = {
          ...loc,
          tags: newLocationTags,
          edges: updatedEdges,
        };

        locationUpdates.push({
          id: loc._id!,
          changes: updatedLocation,
        });
      }
    }

    return {
      locationUpdates,
      edgeUpdates,
      cameraUpdates,
    };
  }

  public getAppleTvList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationActions.getAppleTvList),
      switchMap(() => {
        return this.appleTvService
          .getAll()
          .pipe(
            switchMap(res => {
              return [
                LocationActions.getAppleTvSuccess({ documents: res }),
              ];
            }),
            catchError(response => {
              return [
                LocationActions.getAppleTvFail(),
              ];
            }),
            share(),
          );
      }),
    ),
  );


  public getOrganizationGeneaDoors$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationActions.getOrganizationAccessDoors),
      mergeMap(({ sourceType }) => {
        return this.accessDoorsService.getOrganizationAccessDoors(sourceType)
          .pipe(
            switchMap(res => {
              return [
                LocationActions.getOrganizationDoorsSuccess({ documents: res }),
              ];
            }),
            catchError(() => {
              return of(
                LocationActions.getOrganizationDoorsFail(),
              );
            }),
          );
      }),
    ),
  );

  public getRemoteIOList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationActions.getRemoteIOs),
      switchMap(() => {
        return this.remoteIOService
          .getAll({})
          .pipe(
            switchMap(res => {
              return [
                LocationActions.getRemoteIOsSuccess({ documents: res }),
              ];
            }),
            catchError(response => {
              return [
                LocationActions.getRemoteIOsFail(),
              ];
            }),
            share(),
          );
      }),
    ),
  );

  public getExternalStorageList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationActions.getExternalStorage),
      switchMap(() => {
        return this.externalStorageService
          .getAll({})
          .pipe(
            switchMap(res => {
              return [
                LocationActions.getExternalStorageSuccess({ documents: res }),
              ];
            }),
            catchError(response => {
              return [
                LocationActions.getExternalStorageFail(),
              ];
            }),
            share(),
          );
      }),
    ),
  );

  public getPhysicalAccessControlList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationActions.getPhysicalAccessControl),
      switchMap(() => {
        return this.physicalAccessControlService
          .getAll({})
          .pipe(
            switchMap((res) => [LocationActions.getPhysicalAccessControlSuccess({ documents: res })]),
            catchError(() => [LocationActions.getPhysicalAccessControlFail()]),
            share(),
          );
      }),
    ),
  );

  public startLoadDevices$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationActions.startLoadDevices),
      concatLatestFrom(() => [
        this.store.pipe(select(LocationSelectors.selectDevicePageLoaded)),
      ]),
      /**
       * Dont call all this requests if they are already loaded in overview
       * Fix double call when go from overview to a device list
       */
      exhaustMap(([, dataLoaded]) => {
        return dataLoaded ? [] : [
          LocationActions.getAppleTvList(),
          LocationActions.getOrganizationAccessDoors({}),
          LocationActions.ResetSensors(),
          LocationActions.GetSensors(),
          LocationActions.getRemoteIOs(),
          LocationActions.getExternalStorage(),
          LocationActions.getPhysicalAccessControl(),
          LocationActions.getOrganizationDeviceOrdering(),
        ];
      }),
    ),
  );

  public saveOrderKeys$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationActions.saveOrderKeysToStore),
      concatLatestFrom(() => [
        this.store.pipe(select(LocationSelectors.selectDeviceIdOrderKey)),
      ]),
      exhaustMap(([{ orderedIds, idTypeMap, shiftIndex }, currentOrdering]) => {
        const idOrderKey: Record<string, number> = { ...currentOrdering };
        orderedIds.forEach((id, orderKey) => {
          const type = idTypeMap[id];
          switch (type) {
            case NodeType.LOCATION:
              idOrderKey[id] = orderKey;
              break;
            default:
              idOrderKey[id] = orderKey - shiftIndex;
          }
        });

        return [
          LocationActions.getOrganizationDeviceOrderingSuccess({ documents: idOrderKey }),
        ];
      }),
    ),
  );


  public saveOrderKeysBackendCall$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationActions.saveOrderKeysBackendCall),
      exhaustMap(({ orderedIds, shiftIndex, idTypeMap, idOrderKey: explicitOrderKey }) => {
        const idOrderKey: Record<string, number> = { ...(explicitOrderKey ?? {}) };
        if (!explicitOrderKey) {
          orderedIds.forEach((id, orderKey) => {
            const type = idTypeMap[id];
            switch (type) {
              case NodeType.LOCATION:
                idOrderKey[id] = orderKey;
                break;
              default:
                idOrderKey[id] = orderKey - shiftIndex;
            }
          });
        }
        return this.locationService
          .updateDeviceOrdering(idOrderKey)
          .pipe(
            switchMap(res => {
              return [
                LocationActions.saveOrderKeysBackendCallSuccess(),
                SharedActions.showMessage({ success: 'Order has been saved' }),
              ];
            }),
            catchError(response => {
              return [
                LocationActions.saveOrderKeysBackendCallFail(),
              ];
            }),
          );
      }),
      share(),
    ),
  );


  public getOrganizationDeviceOrdering$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationActions.getOrganizationDeviceOrdering),
      exhaustMap(() => {
        return this.locationService
          .getDeviceOrdering()
          .pipe(
            switchMap(res => {
              const toSave: Record<string, number> = {};
              res.forEach(device => {
                toSave[device.deviceId] = device.orderKey;
              });
              return [
                LocationActions.getOrganizationDeviceOrderingSuccess({ documents: toSave }),
              ];
            }),
            catchError(response => {
              return [
                LocationActions.getOrganizationDeviceOrderingFail(),
              ];
            }),
          );
      }),
      share(),
    ),
  );


  GetLocationsForGlobalSearch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationActions.GetLocationsForGlobalSearch),
      withLatestFrom(
        this.store.pipe(select(HomeSelectors.selectLocations)),
      ),
      switchMap(([, normalizedLocationWithCameras]) => {
        return this.locationService.getGlobalSearchLocationsData()
          .pipe(
            switchMap(res => {
              return [
                SearchableActions.FlattenSearchableLocations({
                  locations: res,
                  home: normalizedLocationWithCameras,
                }),
              ];
            }),
            catchError(err => {
              const msg = err?.error?.message || 'uknown error occured get Locations Fail';
              return [
                LocationActions.GetLocationsFail({
                  message: msg,
                }),
                AuthenticationActions.Logout({ params: { msg } }),
              ];
            }),
          );
      }),
      catchError(err => {
        return of(
          LocationActions.GetLocationsFail({
            message: err?.error?.message || 'uknown error occured',
          }),
        );
      }),
    ),
  );


  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private locationService: LocationsService,
    private localStorageService: LocalStorageService,
    private sensorsService: SensorsService,
    private translateService: TranslateService,
    private appleTvService: AppleTvService,
    private accessDoorsService: AccessDoorsService,
    private remoteIOService: RemoteIOService,
    private externalStorageService: ExternalStorageService,
    private physicalAccessControlService: PhysicalAccessControlService,
    private databaseTagsService: DatabaseTagsService,
    private utilsService: UtilsService,
  ) {
  }
}
